import './Footer.scss';

import { b2x } from '@b2x/react/src';

import { useAppContext, useAppStaticContext } from './AppContext';
import { Container } from './Container';
import { FooterContentType } from './contentTypes';
import { NewsletterForm } from './forms/NewsletterForm';
import { t } from './i18n/i18n';
import { SocialIcons } from './SocialIcons';

export interface FooterProps {}

export const Footer = (props: FooterProps) => {
  const footerMenu = b2x.useMenu('MENU_FOOTER');

  const footerContent = b2x.useContent<FooterContentType>('FOOTER_CONTENT');

  const { minimalFooter } = useAppContext();

  return (
    <footer className="footer">
      {!minimalFooter && (
        <>
          <div className="bg-secondary">
            <Container>
              <div className="first-row text-white py-3 pt-lg-5 pb-lg-3">
                <b2x.Row className="justify-content-center" gap={0}>
                  <b2x.Col size={{ xl: 11, xs: 12 }}>
                    <b2x.Row>
                      <b2x.Col className="order-lg-2" size={{ lg: 5, xl: 4, xs: 12 }}>
                        <div className="p-3 p-lg-0 text-start text-white">
                          <div className="footerNewsletter">
                            <h6 className="ff-karla fw-bold text-uppercase">{t('misc.newsletterTitle')}</h6>
                            <div className="small pb-2">{b2x.formatHtml(footerContent?.body.newsletterSubtitle)}</div>
                            <NewsletterForm className="mb-4" emailFieldStyle="inputGroup" source="website-footer" />
                          </div>
                        </div>
                      </b2x.Col>
                      <b2x.Col size={{ lg: 7, xl: 8, xs: 12 }}>
                        <AccordionFooterMenu id="first-footer-accordion" menu={footerMenu} />
                      </b2x.Col>
                    </b2x.Row>
                  </b2x.Col>
                </b2x.Row>
              </div>
              <div className="second-row text-white pb-3 py-lg-5">
                <b2x.Row className="justify-content-center">
                  <b2x.Col size={{ md: 12, xl: 11, xs: 12 }}>
                    <b2x.Row>
                      <b2x.Col className="order-md-2" size={{ md: 4, xl: 4, xs: 12 }}>
                        <div className="footer-social d-flex align-items-center">
                          <h6 className="fw-bold d-none d-lg-block me-3 mb-0 text-uppercase extra-small">
                            {t('misc.followUs')}
                          </h6>
                          <div className="d-flex ps-3">
                            <SocialIcons />
                          </div>
                        </div>
                      </b2x.Col>
                      <b2x.Col size={{ md: 8, xl: 8, xs: 12 }}>
                        <div className="footer-payments mt-3 mt-md-0 d-flex align-items-center">
                          <h6 className="d-none d-lg-block fw-bold me-3 mb-0 text-uppercase extra-small">
                            {t('misc.payments')}
                          </h6>
                          <b2x.PaymentIcons className={'px-3 px-lg-0'} />
                        </div>
                      </b2x.Col>
                    </b2x.Row>
                  </b2x.Col>
                </b2x.Row>
              </div>
            </Container>
          </div>
        </>
      )}
      <div className="bg-primary text-white">
        <Container>
          <b2x.Row className="justify-content-center">
            <b2x.Col size={{ xl: 11, xs: 12 }}>
              {footerContent?.body.copyright && (
                <div className="d-flex align-items-center extra-small px-3 px-lg-0 py-3">
                  {b2x.formatHtml(footerContent.body.copyright)}
                </div>
              )}
            </b2x.Col>
          </b2x.Row>
        </Container>
      </div>
    </footer>
  );
};

interface AccordionFooterMenuProps {
  id: string;
  menu?: b2x.MenuApiDto;
}
const AccordionFooterMenu = ({ id, menu }: AccordionFooterMenuProps) => {
  const { session } = b2x.useAppContext();
  const { showAccountOffcanvas } = useAppStaticContext();
  return (
    <b2x.Accordion
      columnLayoutFrom="lg"
      id={id}
      itemBodyClassName="small pb-3"
      itemButtonClassName="bg-transparent text-white fw-bold mb-lg-1 p-3 p-lg-0"
      itemClassName="bg-transparent"
    >
      {menu?.children.map(
        (column) =>
          column.label && (
            <b2x.AccordionItem id={`accordionItem-${column.id}`} key={column.code} title={column.label}>
              {column.children.map((li) =>
                li.code === 'SITE_CCN_AREA' ? (
                  session?.userLogged ? (
                    li.children.map(
                      (subLi) =>
                        subLi.link && (
                          <div key={subLi.code}>
                            <b2x.router.Link className="text-reset text-decoration-none d-block py-1" to={subLi.link}>
                              {subLi.label}
                            </b2x.router.Link>
                          </div>
                        )
                    )
                  ) : (
                    <div key={li.code}>
                      <b2x.BlankButton className="d-block py-1" onClick={showAccountOffcanvas}>
                        {t('account.login')}
                      </b2x.BlankButton>
                    </div>
                  )
                ) : (
                  li.link && (
                    <div key={li.code}>
                      {li.image ? (
                        <b2x.router.Link className={'mt-2 d-inline-block'} to={li.link}>
                          <b2x.Image {...li.image} fluid width={100} />
                        </b2x.router.Link>
                      ) : (
                        <b2x.router.Link className="text-reset text-decoration-none d-block py-1" to={li.link}>
                          {li.label}
                        </b2x.router.Link>
                      )}
                    </div>
                  )
                )
              )}
            </b2x.AccordionItem>
          )
      )}
    </b2x.Accordion>
  );
};
